import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Gift, Mail, Sparkles } from "lucide-react";

export const Newsletter = () => {
  const [email, setEmail] = useState("");
  const [isSubscribed, setIsSubscribed] = useState(false);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (email) {
      setIsSubscribed(true);
      setEmail("");
      // Here you would typically send the email to your newsletter service
    }
  };

  return (
    <section className="relative py-24 bg-gradient-starfield overflow-hidden">
      {/* Animated Starfield */}
      <div className="absolute inset-0">
        {[...Array(100)].map((_, i) => (
          <div
            key={i}
            className="absolute w-1 h-1 bg-gold rounded-full animate-pulse"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              animationDelay: `${Math.random() * 3}s`,
              animationDuration: `${1 + Math.random() * 2}s`,
            }}
          />
        ))}
      </div>

      <div className="container mx-auto px-4 relative z-10">
        <div className="max-w-4xl mx-auto text-center">
          {!isSubscribed ? (
            <>
              <div className="mb-12 animate-on-scroll">
                <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-gradient-gold mb-6 shadow-glow">
                  <Mail className="w-10 h-10 text-primary" />
                </div>
                
                <h2 className="font-serif text-4xl md:text-5xl font-bold text-gold text-glow mb-6">
                  Blijf in balans
                </h2>
                
                <p className="text-lg text-gold-light max-w-2xl mx-auto mb-8">
                  Ontvang exclusieve tips, rituelen en onze gratis gids: 7 Theerituelen voor meer rust en energie.
                </p>
              </div>

              {/* Lead Magnet */}
              <div className="bg-card/90 backdrop-blur-md rounded-3xl p-8 mb-12 border border-gold/40 animate-on-scroll [animation-delay:0.3s] shadow-glow">
                <div className="flex items-center justify-center gap-3 mb-4">
                  <Gift className="w-8 h-8 text-gold" />
                  <h3 className="font-serif text-2xl font-bold text-gold">
                    Gratis Bonus
                  </h3>
                </div>
                
                <p className="text-gold-light mb-6">
                  Ontvang onze exclusieve <strong>"Gids tot Perfecte Thee Rituelen"</strong> 
                  direct in je inbox. Ontdek 7 krachtige rituelen voor verschillende momenten van de dag.
                </p>
                
                <div className="flex items-center justify-center gap-4 text-sm text-gold/80">
                  <div className="flex items-center gap-2">
                    <Sparkles className="w-4 h-4" />
                    <span>20+ pagina's wijsheid</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Sparkles className="w-4 h-4" />
                    <span>Exclusieve recepten</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Sparkles className="w-4 h-4" />
                    <span>Meditatie tips</span>
                  </div>
                </div>
              </div>

              {/* Newsletter Form */}
              <form onSubmit={handleSubmit} className="animate-on-scroll [animation-delay:0.6s]">
                <div className="flex flex-col sm:flex-row gap-4 max-w-md mx-auto mb-6">
                  <div className="relative flex-1">
                    <Input
                      type="email"
                      placeholder="Jouw e-mailadres"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                      className="bg-white/80 backdrop-blur-md border-gold/50 text-primary placeholder:text-muted-foreground focus:border-gold focus:ring-gold/30 py-4 px-6 rounded-full text-center"
                    />
                    {/* Light beam animation on focus */}
                    <div className="absolute inset-0 rounded-full opacity-0 hover:opacity-20 transition-opacity duration-300 bg-gradient-to-r from-transparent via-gold/30 to-transparent pointer-events-none" />
                  </div>
                  
                  <Button
                    type="submit"
                    className="btn-hero whitespace-nowrap px-8 py-4 shadow-glow hover:shadow-[0_0_60px_hsl(var(--gold)/0.8)]"
                  >
                    <Gift className="w-5 h-5 mr-2" />
                    Ontvang mijn gratis gids
                  </Button>
                </div>
                
                <p className="text-xs text-gold/60 max-w-lg mx-auto">
                  We respecteren je privacy. Geen spam, je kunt je altijd afmelden. 
                  Door je aan te melden ga je akkoord met onze privacyvoorwaarden.
                </p>
              </form>

              {/* Benefits */}
              <div className="grid sm:grid-cols-3 gap-6 mt-12 animate-on-scroll [animation-delay:0.8s]">
                <div className="text-center">
                  <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-gold/20 flex items-center justify-center">
                    <span className="text-gold font-bold">📚</span>
                  </div>
                  <p className="text-sm text-gold-light">Exclusieve wellness tips</p>
                </div>
                
                <div className="text-center">
                  <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-gold/20 flex items-center justify-center">  
                    <span className="text-gold font-bold">🎁</span>
                  </div>
                  <p className="text-sm text-gold-light">Speciale aanbiedingen</p>
                </div>
                
                <div className="text-center">
                  <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-gold/20 flex items-center justify-center">
                    <span className="text-gold font-bold">✨</span>
                  </div>
                  <p className="text-sm text-gold-light">Nieuwe producten eerst</p>
                </div>
              </div>
            </>
          ) : (
            /* Success State */
            <div className="animate-on-scroll">
              <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-gradient-gold mb-6 shadow-glow animate-pulse-glow">
                <Sparkles className="w-10 h-10 text-primary" />
              </div>
              
              <h2 className="font-serif text-4xl md:text-5xl font-bold text-gold text-glow mb-6">
                Welkom bij de Lunssence Familie! 🌸
              </h2>
              
              <p className="text-lg text-gold-light max-w-2xl mx-auto mb-8">
                Bedankt voor je aanmelding! Check je inbox - je gratis 
                <strong> "Gids tot Perfecte Thee Rituelen"</strong> staat klaar voor je.
              </p>
              
              <div className="bg-card/10 backdrop-blur-sm rounded-2xl p-6 max-w-lg mx-auto border border-gold/20">
                <p className="text-gold/80 text-sm">
                  💌 E-mail niet ontvangen? Check je spam folder of neem contact met ons op.
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};